// TODO: reduce or simplify colors attributes
import { DeviceOperationalState } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import {
  CheckCircleOutlined,
  EngineeringOutlined,
  FlagOutlined,
  HandymanOutlined,
  RemoveCircleOutlineOutlined,
  type SvgIconComponent,
} from '@mui/icons-material';
import { indigo, red } from '@mui/material/colors';

export type DeviceOperationalStateAttributes = {
  value: DeviceOperationalState;
  label: string;
  order: number;
  color: string;
  contrastColor: string;
  bgcolor: string;
  fgcolor: string;
  Icon: SvgIconComponent;
  fontWeight: number;
  borderColor: string;
  markAreaColor: string;
};

type AttributesMap = Record<DeviceOperationalState, DeviceOperationalStateAttributes>;

const getAttributesMap = (i18n: I18n): AttributesMap => ({
  [DeviceOperationalState.Development]: {
    value: DeviceOperationalState.Development,
    label: t(i18n)`En producción`,
    order: 0,
    color: indigo[50],
    contrastColor: '#000000',
    bgcolor: indigo[50],
    fgcolor: '#000000',
    Icon: EngineeringOutlined,
    fontWeight: 700,
    borderColor: '#ffffff00',
    markAreaColor: indigo[50] + '56',
  },
  [DeviceOperationalState.Initialization]: {
    value: DeviceOperationalState.Initialization,
    label: t(i18n)`Puesta en marcha`,
    order: 1,
    color: indigo[200],
    contrastColor: '#000000',
    bgcolor: indigo[200],
    fgcolor: '#000000',
    Icon: FlagOutlined,
    fontWeight: 700,
    borderColor: '#ffffff00',
    markAreaColor: indigo[200] + '56',
  },
  [DeviceOperationalState.Active]: {
    value: DeviceOperationalState.Active,
    label: t(i18n)`Activo`,
    order: 2,
    color: '#ffffff',
    contrastColor: '#000000',
    bgcolor: '#222b36',
    fgcolor: '#ffffff',
    Icon: CheckCircleOutlined,
    fontWeight: 700,
    borderColor: '#ffffff68',
    markAreaColor: '#00000000',
  },
  [DeviceOperationalState.Maintenance]: {
    value: DeviceOperationalState.Maintenance,
    label: t(i18n)`En mantención`,
    order: 3,
    color: red[200],
    contrastColor: '#000000',
    bgcolor: red[200],
    fgcolor: '#000000',
    Icon: HandymanOutlined,
    fontWeight: 700,
    borderColor: '#ffffff00',
    markAreaColor: red[200] + '56',
  },
  [DeviceOperationalState.OutOfService]: {
    value: DeviceOperationalState.OutOfService,
    label: t(i18n)`Fuera de servicio`,
    order: 4,
    color: red[600],
    contrastColor: '#000000',
    bgcolor: red[600],
    fgcolor: '#000000',
    Icon: RemoveCircleOutlineOutlined,
    fontWeight: 700,
    borderColor: '#ffffff00',
    markAreaColor: red[200] + '56',
  },
});

/**
 * Retrieves the attributes associated with a specific `DeviceOperationalState` key.
 *
 * @param key - The `DeviceOperationalState` key for which attributes are requested.
 * @param i18n - The i18n instance used for localization.
 * @returns The attributes corresponding to the provided key.
 * @throws If the key is not found in the attributes map, an error is thrown.
 */
export const getDeviceOperationalStateAttributes = (
  key: DeviceOperationalState,
  i18n: I18n,
): DeviceOperationalStateAttributes => {
  const attributesMap = getAttributesMap(i18n);
  if (!(key in attributesMap)) {
    throw new Error(`Key ${key} not found in the DeviceOperationalState attributes map`);
  }
  return attributesMap[key];
};

/**
 * Retrieves a list of all attributes defined in the attributes map, sorted by their precedence.
 *
 * @param i18n - The i18n instance used for localization.
 * @returns An array of all `DeviceOperationalStateAttributes`, sorted by the `order` property.
 */
export const getAllDeviceOperationalStateAttributes = (
  i18n: I18n,
): DeviceOperationalStateAttributes[] => {
  const attributesMap = getAttributesMap(i18n);
  return Object.values(attributesMap).sort((a, b) => a.order - b.order);
};

export const isDeviceOperationalState = (key: unknown): key is DeviceOperationalState => {
  const operationalStates = Object.values(DeviceOperationalState);
  return operationalStates.some((state) => state === key);
};
